import { Upload, message } from 'antd';
import type { GetProp, UploadProps } from 'antd';
import ImgCrop from 'antd-img-crop';
import type { ImgCropProps } from 'antd-img-crop';
import { commonApis } from '@/configs/apis/common';
import { Loading } from '@/components/loading';
import CameraIcon from '@/assets/images/common/svg/camera.svg?react';
import { UploadFileCategory } from '@/configs/apis/type';
import styles from './index.module.scss';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

interface UploadAvatarProps extends Omit<UploadProps, 'onChange' | 'children'> {
  value?: string;
  onChange?: (value: string) => void;
  children?:
    | React.ReactNode
    | ((value: { loading: boolean; imageUrl: string | undefined }) => React.ReactNode);
}
const UploadAvatar = (props: UploadAvatarProps) => {
  const {
    value,
    onChange,
    beforeUpload: beforeUpload_,
    children,
    className,
    ...retsProps
  } = props;
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();

  const beforeUpload = async (file: FileType, fileList: FileType[]) => {
    const checkType = [
      'image/jpg',
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/gif',
    ];
    const isOk = checkType.includes(file.type);
    if (!isOk) {
      message.error(`Please check if the file suffix is .jpg/.jpeg/.png/.webp/.gif`);
      return false;
    }

    const clipFile = await beforeUpload_?.(file, fileList);
    if (clipFile && typeof clipFile === 'string') {
      return false;
    }

    const isLt100KB = (clipFile as File)?.size / 1024 < 100;
    if (!isLt100KB) {
      message.error('Image must smaller than 100kB!');
      return false;
    }

    return Promise.resolve(clipFile);
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      setLoading(false);
    }

    if (info.file.status === 'error') {
      setLoading(false);
    }
  };

  useEffect(() => {
    setImageUrl(value);
  }, [value]);

  return (
    <Upload
      listType="picture-circle"
      className={cls(styles.avatarUploader, className)}
      showUploadList={false}
      customRequest={async ({ file, onError, onSuccess }: any) => {
        try {
          const data = await commonApis.uploadFile({
            file,
            category: UploadFileCategory.AVATAR,
          });
          onChange?.(data?.access_url);
          setImageUrl(data?.access_url);
          onSuccess(data);
        } catch (error) {
          onError(error);
        }
      }}
      beforeUpload={beforeUpload}
      onChange={handleChange}
      {...retsProps}
    >
      {children ? (
        typeof children === 'function' ? (
          children({ loading, imageUrl })
        ) : (
          children
        )
      ) : (
        <div className={styles.uploadBox}>
          {loading ? (
            <Loading text={null} />
          ) : imageUrl ? (
            <div className={styles.uploadView}>
              <img src={imageUrl} alt="avatar" />
              <div className={styles.uploadMask}>
                <CameraIcon className={styles.icon} />
              </div>
            </div>
          ) : (
            <CameraIcon className={styles.icon} />
          )}
        </div>
      )}
    </Upload>
  );
};
export default UploadAvatar;

interface UploadAvatarCropProps extends Omit<ImgCropProps, 'children'> {
  avatar?: string;
  children?: JSX.Element;
  uploadProps?: UploadAvatarProps;
}
export const UploadAvatarCrop = ({
  avatar,
  children,
  uploadProps = {},
  ...restProps
}: UploadAvatarCropProps) => {
  const { children: uploadChildren, ...restUploadProps } = uploadProps;
  return (
    <ImgCrop
      modalTitle="Edit avatar"
      rotationSlider
      showGrid
      quality={0.2}
      {...restProps}
    >
      {children ? (
        children
      ) : (
        <UploadAvatar
          value={avatar}
          accept=".jpg, .jpeg, .png, .webp, .gif"
          {...restUploadProps}
        >
          {uploadChildren}
        </UploadAvatar>
      )}
    </ImgCrop>
  );
};
